import { TemplatePortal } from '@angular/cdk/portal';
import {
  afterNextRender,
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef
} from '@angular/core';
import { BackButton, TranslatePipe } from '@cmi/addin-core';
import { firstValueFrom } from 'rxjs';
import * as wordControls from '../../../lib/word-content-controls';
import { App } from '../../app';
import { PropertyFormatForm } from '../../property-format/property-format-form';
import { ProviderTypeIcon } from '../../shared/provider-type-icon/provider-type-icon';
import { SearchBar } from '../../shared/search-bar/search-bar';
import { Assoc, ContentProviderProperty, Field, RootType } from '../home.models';
import { HomeService } from '../home.service';

interface PathItem {
  name: string;
  key: string;
}

@Component({
  selector: 'cmi-model-navigation',
  imports: [TranslatePipe, ProviderTypeIcon, BackButton, PropertyFormatForm, SearchBar],
  templateUrl: './model-navigation.html',
  host: { class: 'flex flex-col h-full' }
})
export class ModelNavigation implements OnDestroy {
  private readonly _homeService = inject(HomeService);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _appComponent = inject(App);
  private readonly _footerPortal = viewChild.required<TemplateRef<unknown>>('footerPortal');

  protected readonly searchTerm = signal<string>('');
  protected readonly isLoading = computed(() => this._homeService.rootTypeLoading());
  protected readonly error = computed(() => this._homeService.rootTypeError());
  protected readonly navigationPath = signal<PathItem[]>([]);
  protected readonly currentData = signal<RootType | null>(null);
  protected readonly fetchingData = signal<boolean>(false);
  protected readonly fetchError = signal<string | null>(null);
  protected readonly expandedFieldKey = signal<string | null>(null);

  public constructor() {
    afterNextRender(() => {
      const portal = new TemplatePortal(this._footerPortal(), this._viewContainerRef);
      this._appComponent.footerPortal.set(portal);
    });
  }

  protected readonly getFieldAsProperty = (field: Field): ContentProviderProperty => {
    // Convert Field to ContentProviderProperty for the format form
    return {
      name: field.name,
      key: field.key,
      typeName: field.dataType,
      formatString: undefined
    } as ContentProviderProperty;
  };

  protected readonly supportsFormatting = (field: Field): boolean => {
    return ['DateTime', 'Boolean', 'Int32', 'Numeric', 'Date'].includes(field.dataType);
  };

  protected readonly currentFields = computed(() => {
    const data = this.currentData();
    const search = this.searchTerm().toLowerCase();

    let fields: Field[];
    if (data) {
      fields = data.fields ?? [];
    } else {
      fields = this._homeService.rootType()?.fields ?? [];
    }

    if (search) {
      fields = fields.filter(
        (field) =>
          field.name.toLowerCase().includes(search) || field.key.toLowerCase().includes(search)
      );
    }

    return [...fields].sort((a, b) => this._byName(a, b));
  });

  protected readonly currentAssocs = computed(() => {
    const data = this.currentData();
    const search = this.searchTerm().toLowerCase();

    let assocs: Assoc[];
    if (data) {
      assocs = data.assocs ?? [];
    } else {
      assocs = this._homeService.getAssocs();
    }

    if (search) {
      assocs = assocs.filter(
        (assoc) =>
          assoc.name.toLowerCase().includes(search) || assoc.key.toLowerCase().includes(search)
      );
    }

    return [...assocs].sort((a, b) => this._byName(a, b));
  });

  protected async navigateToAssoc(
    assoc: Assoc,
    allowedType: { key: string; name: string }
  ): Promise<void> {
    this.fetchingData.set(true);
    this.fetchError.set(null);
    this.searchTerm.set('');

    try {
      // Use the selected allowed type to navigate to
      if (!allowedType) {
        return;
      }

      const result = await firstValueFrom(this._homeService.getAllowedTypes(allowedType.key));

      // Add to navigation path
      this.navigationPath.update((path) => [
        ...path,
        { name: assoc.name, key: assoc.key },
        { name: allowedType.name, key: allowedType.key }
      ]);
      this.currentData.set(result);
    } catch (err) {
      console.error('Error fetching association data:', err);
      this.fetchError.set('Failed to load association data');
    } finally {
      this.fetchingData.set(false);
    }
  }

  protected navigateToRoot(): void {
    this.navigationPath.set([]);
    this.currentData.set(null);
  }

  protected async navigateToPathIndex(index: number): Promise<void> {
    const path = this.navigationPath();

    // Path alternates [assoc(even), type(odd), assoc(even), type(odd), ...]
    // Clicking an assoc item (even) → navigate to the parent type (index - 1), or root if first
    // Clicking a type item (odd) → navigate to that type (index)
    const targetIndex = index % 2 === 0 ? index - 1 : index;

    if (index === 0) {
      this.navigateToRoot();
      return;
    }

    const typeKey = path[targetIndex]?.key;
    if (!typeKey) {
      return;
    }

    this.fetchingData.set(true);
    this.fetchError.set(null);
    this.searchTerm.set('');

    try {
      const result = await firstValueFrom(this._homeService.getAllowedTypes(typeKey));
      this.navigationPath.set(path.slice(0, targetIndex + 1));
      this.currentData.set(result);
    } catch (err) {
      console.error('Error navigating to path:', err);
      this.fetchError.set('Failed to load data');
    } finally {
      this.fetchingData.set(false);
    }
  }

  protected toggleFormattedInsert(field: Field, event: Event): void {
    event.stopPropagation();
    const key = this._getFieldKey(field);
    const currentExpanded = this.expandedFieldKey();
    if (currentExpanded === key) {
      // Collapse if already expanded
      this.expandedFieldKey.set(null);
    } else {
      // Expand this field (accordion behavior)
      this.expandedFieldKey.set(key);
    }
  }

  protected isFieldExpanded(field: Field): boolean {
    const key = this._getFieldKey(field);
    return this.expandedFieldKey() === key;
  }

  protected async onApplyFormat(field: Field, formatValue: string): Promise<void> {
    await this._insertFieldWithFormat(field, formatValue);
    this.expandedFieldKey.set(null);
  }

  private _getFieldKey(field: Field): string {
    const pathString = this.navigationPath()
      .map((p) => p.key)
      .join('/');
    return pathString ? `${pathString}/${field.key}` : field.key;
  }

  protected async insertField(field: Field): Promise<void> {
    // Insert directly without format
    await this._insertFieldWithFormat(field, undefined);
  }

  private async _insertFieldWithFormat(field: Field, formatValue?: string): Promise<void> {
    try {
      const pathString = this.navigationPath()
        .map((p) => p.key)
        .join('/');
      const fullPath = pathString ? `${pathString}` : '';
      const title = formatValue
        ? `CMI_XPathObj.${field.key}:${formatValue}`
        : `CMI_XPathObj.${field.key}`;

      await wordControls.insertRichTextControl({
        title,
        tag: { objpath: fullPath !== '' ? `Dokument/${fullPath}` : 'Dokument' },
        placeholder: field.name
      });
    } catch (err) {
      console.error('Error inserting content control:', err);
      // Error is read-only, just log it
    }
  }

  protected async insertAssoc(assoc: Assoc, pathKey: string): Promise<void> {
    try {
      const pathString = this.navigationPath()
        .map((p) => p.key)
        .join('/');
      const fullPath = pathString ? `${pathString}/${assoc.key}` : assoc.key;

      // Use table control for associations with multiplicityMax > 1 (multiple items)
      if (assoc.multiplicityMax > 1) {
        await wordControls.insertTableControl({
          title: 'CMI_XPathList',
          tag: { objpath: `Dokument/${fullPath}/${pathKey}` },
          placeholder: assoc.name
        });
      }
    } catch (err) {
      console.error('Error inserting content control:', err);
      // Error is read-only, just log it
    }
  }

  private _byName<T extends { name: string }>(a: T, b: T): number {
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  }

  public ngOnDestroy(): void {
    this._appComponent.footerPortal.set(null);
  }

  public onSearchTermChanged(term: string): void {
    this.searchTerm.set(term);
  }
}
